import React, { useContext } from 'react';
import { useWizard } from 'react-use-wizard';

import { FlowTypeContext } from './FlowTypeProvider';
import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from './NewSupergraphOnboardingControls';
import { SupergraphOnboardingStep } from './shared';

export const NewSupergraphOnboardingFooter = ({
  loading,
  creatingSupergraph,
  createSupergraph,
  nextButtonState,
  createSupergraphButtonState,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
}: {
  loading: boolean;
  creatingSupergraph: boolean;
  createSupergraph: () => void;
  nextButtonState: ButtonState;
  createSupergraphButtonState: ButtonState;
  continueAnalyticsLabelsByStep: Record<SupergraphOnboardingStep, string>;
  whenStepsBegan: Partial<Record<SupergraphOnboardingStep, number>> | undefined;
}) => {
  const { isFirstStep, isLastStep, activeStep, nextStep } = useWizard();
  const { flowType } = useContext(FlowTypeContext);

  // Define boolean variables to represent different flow types and states
  const isDedicatedPrivate = flowType === 'DEDICATED_PRIVATE';

  // Decide when to show the Cancel Button: Show it unless it's the last step of the 'DEDICATED_PRIVATE' flow
  const showCancelButton = !isDedicatedPrivate || !isLastStep;

  // Decide when to show the Back Button: Show it if it's not the first step and depending on the flow type
  const showBackButton =
    !isFirstStep &&
    ((isDedicatedPrivate && !isLastStep) || !isDedicatedPrivate);

  // Decide when to show the Create Graph Button: Show it when it's the last step and the flow type is not 'DEDICATED_PRIVATE'
  const showCreateGraphButton = isLastStep && !isDedicatedPrivate;

  // Decide when to show the Next Button: Show it on all steps except the last step of the 'DEDICATED_PRIVATE' flow
  const showNextButton =
    !isLastStep && !(activeStep === 3 && isDedicatedPrivate);

  return (
    <div className="fixed bottom-0 z-10 flex w-full items-center justify-between border-t border-primary bg-white p-4 3xl:hidden">
      {/* Render the Cancel Button if the condition is met */}
      {showCancelButton && (
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />
      )}

      {/* Render the Back Button if the condition is met */}
      {showBackButton && (
        <NewSupergraphOnboardingControls.BackButton
          creatingSupergraph={creatingSupergraph}
          className="ml-auto"
        />
      )}

      {/* Render the Create Graph Button for non-'DEDICATED_PRIVATE' flows if it's the last step */}
      {showCreateGraphButton && (
        <NewSupergraphOnboardingControls.CreateGraphButton
          smallScreen={true}
          creatingSupergraph={creatingSupergraph}
          state={createSupergraphButtonState}
          onClick={createSupergraph}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
          whenStepsBegan={whenStepsBegan}
        />
      )}

      {/* Render the Create Graph Button for 'DEDICATED_PRIVATE' flows on the 3rd active step */}
      {activeStep === 3 && isDedicatedPrivate && (
        <NewSupergraphOnboardingControls.CreateGraphButton
          smallScreen={true}
          creatingSupergraph={creatingSupergraph}
          state={createSupergraphButtonState}
          onClick={() => {
            createSupergraph();
            nextStep();
          }}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
          whenStepsBegan={whenStepsBegan}
        />
      )}

      {/* Render the Next Button if none of the above conditions are met */}
      {showNextButton && (
        <NewSupergraphOnboardingControls.NextButton
          smallScreen={true}
          creatingSupergraph={creatingSupergraph}
          state={nextButtonState}
          loading={loading}
          whenStepsBegan={whenStepsBegan}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
      )}
    </div>
  );
};
